import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";

import { Disclose } from "@lopie/kernel-ui";

import {
  pollDelay, readProgress, waitingOn, whoIs,
} from "./approval.js";

// The approve dialog's half of the sandbox gate (LO-191): what is said when the
// kernel refuses an approval, and the window that waits while a narrowed decision
// is re-tested. The rules about what a response means are `approval.js`; this is
// layout and the one timer.

/** Long unbreakable strings are most of what is on this screen; see Requests.jsx. */
const WRAPS = { "& code": { overflowWrap: "anywhere" } };

function Label({ children }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
      {children}
    </Typography>
  );
}

const short = (hash) => String(hash || "").slice(0, 12);

/**
 * The gate refused the approval: the kernel's own sentence, what is missing, and
 * the one thing to do about it.
 *
 * An `Alert` with `role="alert"`, because it is the answer to the button that was
 * just pressed and a person using a screen reader has to hear it. Said in words
 * and never only as a colour, and never as "no results": the title names the
 * state, `detail` is the kernel's, and `next` is a step. The approver is still in
 * the dialog with every line as they left it, so they can try again.
 */
export function GateRefusal({ refusal }) {
  const ref = useRef(null);
  // The button that was pressed is at the foot of a dialog that scrolls and this
  // sits at the top, so without this a sighted approver presses it and sees
  // nothing happen. Focus stays on the button; the alert role announces it.
  useEffect(() => {
    if (refusal) ref.current?.scrollIntoView?.({ block: "nearest" });
  }, [refusal]);
  if (!refusal) return null;
  return (
    <Alert ref={ref} severity="error" variant="outlined" role="alert">
      <AlertTitle>Not approved: {refusal.title}</AlertTitle>
      <Stack spacing={1}>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {refusal.detail}
        </Typography>
        {refusal.failed.length ? (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              The failing {refusal.failed.length === 1 ? "check" : "checks"}
            </Typography>
            <FailedChecks checks={refusal.failed} />
          </Box>
        ) : null}
        {refusal.runId || refusal.manifestHash ? (
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {refusal.runId ? <>Run <code>{refusal.runId}</code>. </> : null}
            {refusal.buildId ? <>Build <code>{refusal.buildId}</code>. </> : null}
            {refusal.manifestHash ? <>Manifest <code>{short(refusal.manifestHash)}</code>. </> : null}
            Nothing was granted and the request is still pending.
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Nothing was granted and the request is still pending.
          </Typography>
        )}
        <Typography variant="body2">
          <strong>What to do.</strong> {refusal.next}
        </Typography>
      </Stack>
    </Alert>
  );
}

/** The failing checks of a run, as the kernel named them (`gate.failed_checks`). */
function FailedChecks({ checks }) {
  if (!checks?.length) return null;
  return (
    <Box component="ul" aria-label="failing sandbox checks" sx={{ m: 0, pl: 3 }}>
      {checks.map((c, i) => (
        <Typography component="li" variant="body2" key={`${c.persona}:${c.id}:${i}`} sx={{ overflowWrap: "anywhere" }}>
          <code>{c.id || "check"}</code>
          {c.title ? ` — ${c.title}` : ""}
          {c.persona ? <>, as <code>{c.persona}</code></> : ""}
          {c.route ? <> on <code>{c.route}</code></> : ""}
          {c.detail ? `: ${c.detail}` : ""}
        </Typography>
      ))}
    </Box>
  );
}

/** The decision this window recorded: whose, when, why, and what it would land. */
function Decision({ decision, renderCeiling }) {
  if (!decision) return null;
  return (
    <Stack spacing={0.75}>
      <Label>Whose decision</Label>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        <code>{whoIs(decision.decided_by) || "a governor"}</code>
        {decision.recorded_at ? `, recorded ${decision.recorded_at}` : ""}.
        {decision.reason ? <> Reason given: &ldquo;{decision.reason}&rdquo;.</> : " No reason was given."}{" "}
        It is the latest decision on this request: a newer approval by anyone
        replaces it, and only the latest can land.
      </Typography>
      {decision.approved && renderCeiling ? (
        <Disclose
          summary="What lands, if the run passes, is the narrowed manifest."
          label="Show it"
        >
          {renderCeiling(decision.approved)}
        </Disclose>
      ) : null}
    </Stack>
  );
}

/**
 * Waiting for a narrowed decision's re-run, and reading the request while it does.
 *
 * ## Why this reads the request
 *
 * In enforce mode an approval that strikes a line is **recorded and not written**
 * (a 202). The run it waits for ends in a function that cannot write a manifest,
 * so something else lands the approval: the kernel's own sweep, every minute, with
 * nobody reading (LO-196), or a read of the request after the run passes. This
 * window no longer has to be that read, and it still reads, for two reasons: to
 * show the run as it goes, and because what an approval is followed by (releasing
 * the build, installing the app under the name typed in the approve window) is done
 * from here. It asks every few seconds, backing off, and when the answer says the
 * request is approved, whoever landed it, it does that (`sandbox_gate.py`, "Why
 * completion is not where the run finishes").
 *
 * ## When it stops
 *
 * On the request being approved, rejected, replaced or cancelled; on the re-run
 * failing, being blocked or being overtaken by another decision; and when the
 * dialog closes. A window that is not open reads nothing.
 *
 * It renders the dialog's body and its buttons together, because which buttons
 * there are depends on where the decision is.
 *
 * Props: `get` reads the kernel; `stage` is `{requestId, decisionId, accepted,
 * view, immediate}` (the 202 body, or the request view when the window is
 * reopened from the card); `active` is whether the dialog is open;
 * `onLanded(view, progress)` is called once, with the request that is now
 * approved, and the caller does what an approval is followed by.
 * `onStopped(progress)` is called when the window stops for any other reason.
 */
export function RerunPanel({
  get, stage, active, appType, renderCeiling,
  onLanded, onStopped, onBackToLines, onAsRequested, onReject, onClose,
}) {
  const initial = stage.accepted
    ? readProgress({
      state: "pending",
      approval_pending: stage.accepted.decision,
      approval_status: { outcome: "waiting", waiting_for: stage.accepted.waiting_for },
    }, { decisionId: stage.decisionId })
    : readProgress(stage.view, { decisionId: stage.decisionId });
  const [progress, setProgress] = useState(initial);
  // The request's own `approval_pending` carries the narrowed ceiling in the shape
  // a card renders; the 202's `decision` carries it raw.
  const [decision, setDecision] = useState(
    stage.accepted?.request?.approval_pending || stage.accepted?.decision || stage.view?.approval_pending || null,
  );
  const [readError, setReadError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [reads, setReads] = useState(0);
  const [lastRead, setLastRead] = useState("");
  const [kick, setKick] = useState(0);
  const [landing, setLanding] = useState(false);
  const kicked = useRef(0);
  // Callbacks change on every render of the screen above and the timer must not
  // be reset by that, so the latest are kept where the timer can reach them.
  const latest = useRef({ onLanded, onStopped });
  latest.current = { onLanded, onStopped };
  const stopped = progress.stop || landing || Boolean(readError?.fatal);

  useEffect(() => {
    if (!active || stopped) return undefined;
    const now = kick !== kicked.current;
    kicked.current = kick;
    const delay = now ? 0 : pollDelay(attempt, { immediate: Boolean(stage.immediate) });
    let live = true;
    const timer = setTimeout(async () => {
      let body = null;
      try {
        body = await get(`/manifest-requests/${stage.requestId}`);
      } catch (e) {
        if (!live) return;
        // A refusal to read or a request that is gone will not mend itself.
        const status = Number(e?.status || 0);
        setReadError({ message: e?.message || "the request could not be read", status, fatal: [401, 403, 404].includes(status) });
        setAttempt((n) => n + 1);
        return;
      }
      if (!live) return;
      setReadError(null);
      setReads((n) => n + 1);
      setLastRead(new Date().toLocaleTimeString());
      if (body?.approval_pending) setDecision(body.approval_pending);
      const next = readProgress(body, { decisionId: stage.decisionId });
      setProgress(next);
      if (next.kind === "landed") {
        setLanding(true);
        latest.current.onLanded(body, next);
        return;
      }
      // The window has stopped reading because there is nothing more to wait for
      // (the re-run failed, or the decision was overtaken). The card behind it
      // should say so too.
      if (next.stop) latest.current.onStopped?.(next);
      setAttempt((n) => n + 1);
    }, delay);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [active, stopped, attempt, kick, get, stage.requestId, stage.decisionId, stage.immediate]);

  const checkNow = () => {
    setAttempt(0);
    setKick((n) => n + 1);
  };

  const waitingFor = progress.waitingFor || stage.accepted?.waiting_for || null;
  const warnings = stage.accepted?.does_not_prove || [];
  const closeOnly = (
    <Button onClick={onClose} disabled={landing}>Close</Button>
  );

  let body;
  let actions = closeOnly;

  if (landing || progress.kind === "landed") {
    body = (
      <Alert severity="success" variant="outlined" role="status">
        <AlertTitle>The re-run passed and the approval has landed</AlertTitle>
        <Typography variant="body2">
          Releasing the build and starting the install from here. This takes a
          few seconds.
        </Typography>
      </Alert>
    );
  } else if (progress.kind === "failed") {
    body = (
      <Stack spacing={1.5}>
        <Alert severity="error" variant="outlined" role="status">
          <AlertTitle>The sandbox failed the narrowed manifest</AlertTitle>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {progress.failure?.detail || "The newest run for the narrowed manifest failed."}
            </Typography>
            <FailedChecks checks={progress.failure?.failed_checks} />
            <Typography variant="body2">
              The request is still pending and nothing is granted. Your recorded
              decision does not land. A failed run cannot be passed over: there
              is no override.
            </Typography>
          </Stack>
        </Alert>
        <Typography variant="body2">
          <strong>What to do.</strong> Approve with a different ceiling, which
          is re-tested the same way; approve exactly what was asked for, which
          is judged against the run that got the request here; or reject with a
          reason.
        </Typography>
        <Decision decision={decision} renderCeiling={renderCeiling} />
      </Stack>
    );
    actions = (
      <>
        <Button onClick={onClose}>Close</Button>
        <Button variant="outlined" color="inherit" onClick={onReject}>Reject…</Button>
        <Button variant="outlined" onClick={onAsRequested}>Approve as requested instead…</Button>
        <Button variant="contained" onClick={onBackToLines}>Change what I approve…</Button>
      </>
    );
  } else if (progress.kind === "blocked") {
    body = (
      <Alert severity="warning" variant="outlined" role="status">
        <AlertTitle>The governor who decided no longer governs</AlertTitle>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {progress.blocked?.detail || "The recorded decision will not be landed on their behalf."} Nothing is granted.
          A current governor can approve it again, or reject it.
        </Typography>
      </Alert>
    );
  } else if (progress.kind === "replaced") {
    body = (
      <Alert severity="info" variant="outlined" role="status">
        <AlertTitle>A newer decision replaced yours</AlertTitle>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          <code>{whoIs(progress.by) || "Another governor"}</code> approved this request
          {progress.at ? ` at ${progress.at}` : ""} with another ceiling. There is one
          slot for a waiting decision and a newer approval takes it, so yours will not
          land and the sandbox is re-testing theirs. Nothing is granted yet.
        </Typography>
      </Alert>
    );
  } else if (progress.kind === "overtaken") {
    body = (
      <Alert severity="info" variant="outlined" role="status">
        <AlertTitle>The decision that waited was overtaken</AlertTitle>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          {progress.detail || "A newer decision, or a rejection, cancellation or replacement, overtook it."}{" "}
          Yours will not land. Read the card for where the request is now.
        </Typography>
      </Alert>
    );
  } else if (progress.kind === "closed") {
    const word = { rejected: "rejected", superseded: "replaced by a newer request", cancelled: "cancelled" }[progress.state] || progress.state;
    body = (
      <Alert severity="info" variant="outlined" role="status">
        <AlertTitle>The request was {word} while the decision waited</AlertTitle>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          Nothing was granted.
          {progress.by ? <> By <code>{whoIs(progress.by)}</code>.</> : null}
          {progress.reason ? ` Reason: ${progress.reason}` : ""}
          {progress.supersededBy ? <> The newer request is <code>{progress.supersededBy}</code>.</> : null}
        </Typography>
      </Alert>
    );
  } else if (progress.kind === "no_decision") {
    body = (
      <Alert severity="warning" variant="outlined" role="status">
        <AlertTitle>No decision is recorded on this request</AlertTitle>
        <Typography variant="body2">
          {progress.detail} Nothing is granted. Approve again to start over.
        </Typography>
      </Alert>
    );
  } else {
    const errored = progress.kind === "settle_error";
    body = (
      <Stack spacing={1.5}>
        <Alert severity={errored ? "warning" : "info"} variant="outlined" role="status">
          <AlertTitle>
            {errored
              ? "The run passed and the approval could not be written yet"
              : "The sandbox is re-testing the narrowed manifest"}
          </AlertTitle>
          <Typography variant="body2">
            {errored
              ? `${progress.detail || "Nothing is granted and the decision is still recorded."} This window reads the request again, which retries it.`
              : <>Your approval of <code>{appType}</code> is recorded and has
                granted nothing. It lands when a sandbox run passes for exactly
                the manifest you narrowed it to.</>}
          </Typography>
        </Alert>

        <Box>
          <Label>Waiting for</Label>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            A run that passes for build{" "}
            <code>{waitingFor?.build_id || decision?.build_id}</code> and the
            narrowed manifest{" "}
            <code>{short(waitingFor?.manifest_hash || decision?.manifest_hash)}</code>
            {waitingFor?.proposed_manifest_hash || decision?.proposed_hash
              ? <> (the request as asked is <code>{short(waitingFor?.proposed_manifest_hash || decision?.proposed_hash)}</code>)</>
              : null}
            . A pass for any other build or manifest does not count.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }} aria-live="polite">
            <strong>Now.</strong> {errored ? "The run passed; the write is being retried." : waitingOn(waitingFor)}
          </Typography>
        </Box>

        <Decision decision={decision} renderCeiling={renderCeiling} />

        <Box>
          <Label>If you close this window</Label>
          <Typography variant="body2">
            The decision stays recorded and is not cancelled, and it does not
            need this window or anybody reading it: the platform lands it by
            itself, as you decided it and with your name on it, within about a
            minute of the run passing. What it cannot do for you is the name
            the app will answer on, which is typed here, so the build is
            released and the app installed from this screen only while it is
            open when the approval lands. If it lands while this is closed the
            app is approved and not installed: the card says so and has one
            button, Release and install, that finishes it. To change your mind,
            approve again with another ceiling or reject.
          </Typography>
        </Box>

        {warnings.length ? (
          <Stack spacing={0.5}>
            {warnings.map((line) => (
              <Typography variant="caption" color="text.secondary" key={line}>
                {line}
              </Typography>
            ))}
          </Stack>
        ) : null}
      </Stack>
    );
    actions = (
      <>
        <Button onClick={onClose}>Close</Button>
        <Button variant="outlined" onClick={checkNow}>Check now</Button>
      </>
    );
  }

  const meta = !landing && ["waiting", "settle_error"].includes(progress.kind) ? (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      {reads
        ? `Read the request ${reads} ${reads === 1 ? "time" : "times"}; last at ${lastRead}. `
        : "Not read yet. "}
      {stopped ? "Not reading any more." : "Reading again shortly, and less often the longer this takes."}
    </Typography>
  ) : null;

  return (
    <>
      <DialogContent dividers sx={WRAPS}>
        <Stack spacing={2}>
          {body}
          {readError ? (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>The request could not be read just now</AlertTitle>
              <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                {readError.message}.{" "}
                {readError.fatal
                  ? "This will not mend by reading again, so this window has stopped. "
                  : "Nothing has changed and the decision is still recorded. Trying again. "}
                {readError.status === 404 ? "The request is no longer there." : ""}
              </Typography>
            </Alert>
          ) : null}
          {meta}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>{actions}</DialogActions>
    </>
  );
}
